import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { OptimizedImage } from "@/components/ui/optimized-image"
import type { SeoMeta } from "@/types"
import { LOCATIONS, LOCATION_SLUGS } from "@/data/directory/registry"
import { AREA_GUIDES } from "@/data/areas/registry"
import { LOCATION_IMAGES, imageSrcSet } from "@/data/location-images"
import { cdnUrl } from "@/lib/cdn"

/* ------------------------------------------------------------------ */
/* Icons                                                               */
/* ------------------------------------------------------------------ */

function IconMapPin({ className = "size-5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
      <path
        fillRule="evenodd"
        d="m9.69 18.933.003.001C9.89 19.02 10 19 10 19s.11.02.308-.066l.002-.001.006-.003.018-.008a5.741 5.741 0 0 0 .281-.14c.186-.096.446-.24.757-.433.62-.384 1.445-.966 2.274-1.765C15.302 14.988 17 12.493 17 9A7 7 0 1 0 3 9c0 3.492 1.698 5.988 3.355 7.584a13.731 13.731 0 0 0 2.273 1.765 11.842 11.842 0 0 0 .976.544l.062.029.018.008.006.003ZM10 11.25a2.25 2.25 0 1 0 0-4.5 2.25 2.25 0 0 0 0 4.5Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function IconArrowRight({ className = "size-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
      <path
        fillRule="evenodd"
        d="M3 10a.75.75 0 0 1 .75-.75h10.638L10.23 5.29a.75.75 0 1 1 1.04-1.08l5.5 5.25a.75.75 0 0 1 0 1.08l-5.5 5.25a.75.75 0 1 1-1.04-1.08l4.158-3.96H3.75A.75.75 0 0 1 3 10Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

/* ------------------------------------------------------------------ */
/* Location card                                                       */
/* ------------------------------------------------------------------ */

function AreaLocationCard({ slug }: { slug: string }) {
  const location = LOCATIONS[slug]
  const guides = AREA_GUIDES[slug] ?? []
  if (!location || guides.length === 0) return null

  const previewAreas = guides.slice(0, 4)
  const moreCount = guides.length - previewAreas.length

  return (
    <Link
      href={`/areas/${slug}`}
      className="group flex flex-col overflow-hidden rounded-xl border border-stone bg-white transition-all hover:border-stone/80 hover:shadow-md"
    >
      {LOCATION_IMAGES[slug] && (
        <div className="relative h-44 overflow-hidden">
          <OptimizedImage
            src={cdnUrl(LOCATION_IMAGES[slug].url)}
            srcSet={imageSrcSet(LOCATION_IMAGES[slug].url)}
            sizes="(max-width: 640px) 100vw, 50vw"
            alt={LOCATION_IMAGES[slug].alt}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            width={800}
            height={340}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
        </div>
      )}
      <div className="flex-1 p-8">
        <div className="flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-full bg-red/10">
            <IconMapPin className="size-5 text-red" />
          </div>
          <h2 className="font-display text-2xl font-bold text-charcoal">
            {location.name}
          </h2>
        </div>

        <p className="mt-4 text-sm text-slate">
          {guides.length} neighbourhood guides with prices, transport, schools,
          and local insights.
        </p>

        <div className="mt-5 flex flex-wrap gap-1.5">
          {previewAreas.map((a) => (
            <span
              key={a.slug}
              className="rounded-full bg-stone/50 px-2.5 py-0.5 text-[11px] font-medium text-slate"
            >
              {a.name}
            </span>
          ))}
          {moreCount > 0 && (
            <span className="rounded-full bg-stone/50 px-2.5 py-0.5 text-[11px] font-medium text-warm-grey">
              +{moreCount} more
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-stone px-8 py-4">
        <span className="text-sm font-semibold text-red transition-colors group-hover:text-deep-red">
          Explore {location.name} areas
        </span>
        <IconArrowRight className="size-4 text-red transition-transform group-hover:translate-x-1" />
      </div>
    </Link>
  )
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function AreasIndex({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Area Guides
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Neighbourhood guides<span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            Average prices, transport links, schools, lifestyle, and what
            it&rsquo;s really like to live there. Choose a city to explore.
          </p>
        </div>
      </section>

      {/* Location cards */}
      <section className="border-t border-stone bg-white px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <div className="grid gap-6 sm:grid-cols-2">
            {LOCATION_SLUGS.map((slug) => (
              <AreaLocationCard key={slug} slug={slug} />
            ))}
          </div>
        </div>
      </section>

      {/* SEO copy */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Why area knowledge matters<span className="text-red">.</span>
          </h2>
          <div className="mt-6 space-y-4 text-base leading-relaxed text-slate">
            <p>
              Whether you&rsquo;re buying, selling, or just researching, knowing
              the character of a neighbourhood can make all the difference. Average
              prices, transport links, and school catchments are just the start —
              the feel of a street, the quality of local shops, and the strength
              of the community are what turn a house into a home.
            </p>
            <p>
              Our area guides cover the neighbourhoods we know best: Bristol,
              Brighton, Bath, and Oxford. Each guide is written to give you an
              honest, detailed picture of what living there is really like.
              Looking for an agent? Browse our{" "}
              <Link href="/directory" className="text-red hover:text-deep-red">
                estate agents directory
              </Link>{" "}
              or see our{" "}
              <Link href="/best" className="text-red hover:text-deep-red">
                best estate agents rankings
              </Link>
              .
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-20">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl">
            Are you an estate agent<span className="text-red">?</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-warm-grey">
            Turn your property listings into scroll-stopping video ads. Paste a
            listing, pick a style, and publish across every social platform in
            under a minute.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Request a demo
            </Link>
            <Link
              href="/how-it-works"
              className="inline-flex w-full items-center justify-center rounded-md border border-slate bg-charcoal px-6 py-3 text-base font-medium text-cream transition-colors hover:bg-slate sm:w-auto"
            >
              See how it works
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
